import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Club, UserBasic } from '@hefe/models';
import { ClubSlice } from '@hefe/store';
import {
  Button,
  message,
  Popover,
  Space,
  Spin,
  Typography,
  Upload,
} from 'antd';
import { RcFile } from 'antd/lib/upload';
import { t } from '@hefe/i18n';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';

interface ClubPageHeaderComponentProps {
  club: Club;
}

export default function ClubPageHeaderComponent(
  props: ClubPageHeaderComponentProps
) {
  const { club } = props;
  const dispatch = useAppDispatch();

  const [uploadingImage, setUploadingImage] = useState(false);

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t('errors.avatar_upload.wrong_format'));
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t('errors.avatar_upload.too_large'));
    }

    if (isJpgOrPng && isLt2M) {
      uploadImage(file);
    }

    return false;
  };

  const uploadImage = (file: RcFile) => {
    setUploadingImage(true);

    dispatch(ClubSlice.uploadClubAvatar(file))
      .unwrap()
      .finally(() => setUploadingImage(false));
  };

  const deleteImage = () => {
    setUploadingImage(true);

    dispatch(ClubSlice.deleteClubAvatar())
      .unwrap()
      .finally(() => setUploadingImage(false));
  };

  const clubAsBasicUser = (): UserBasic => {
    const nameParts = club.name.split(' ');
    let firstname = '';
    let lastname = '';
    if (nameParts.length > 0) {
      firstname = nameParts[0];
      lastname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    }

    return {
      id: club.id,
      firstname,
      lastname,
      avatar: club.logo,
    };
  };

  return (
    <Space direction="vertical" align="center">
      <Popover
        content={
          <Space direction="vertical">
            <Space>
              <Upload
                accept=".jpg, .jpeg, .png"
                name="user_icon"
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeUpload}
                disabled={uploadingImage}
                multiple={false}
              >
                <Button loading={uploadingImage} icon={<EditOutlined />}>
                  {t('edit')}
                </Button>
              </Upload>
              {club.logo && (
                <Button onClick={() => deleteImage()} icon={<DeleteOutlined />}>
                  {t('delete')}
                </Button>
              )}
            </Space>
            <Typography.Text>max. 2MB - .jpg / .png</Typography.Text>
          </Space>
        }
      >
        <div style={{ cursor: 'pointer' }}>
          {uploadingImage ? (
            <Spin size="large" />
          ) : (
            <UserAvatar user={clubAsBasicUser()} size={150} />
          )}
        </div>
      </Popover>
      <Typography.Title level={3}>{club.name}</Typography.Title>
    </Space>
  );
}
